import React from "react";
import { RadioGroup } from "~/components/ui/RadioGroup";
import type { RootNote } from "~/types";

interface PianoKeyboardProps {
  rootNotes: readonly RootNote[];
  selectedNote: string;
  onNoteSelect: (note: RootNote) => void;
}

/**
 * Piano keyboard component for selecting root notes.
 * Displays black keys (sharps) and white keys (naturals) in a piano-like layout.
 *
 * - Vertical layout: 2 columns (mobile/tablet) - mimics DAW piano roll
 * - Horizontal layout: 2 rows (desktop) - mimics physical keyboard
 *
 * Both layouts are always rendered, with CSS controlling which is visible at different breakpoints.
 */
export default function PianoKeyboard({
  rootNotes,
  selectedNote,
  onNoteSelect,
}: PianoKeyboardProps) {
  const blackKeys = rootNotes.filter((n) => n.note.includes("♯"));
  const whiteKeys = rootNotes.filter((n) => !n.note.includes("♯"));

  // Handle value change from RadioGroup
  const handleNoteChange = (noteValue: string) => {
    const note = rootNotes.find((n) => n.note === noteValue);
    if (note) {
      onNoteSelect(note);
    }
  };

  return (
    <>
      {/* Vertical layout (mobile/tablet) */}
      <RadioGroup
        value={selectedNote}
        onValueChange={handleNoteChange}
        className="w-full md:hidden flex gap-1"
      >
        {/* Left column: Black keys (sharps) */}
        <div className="w-1/2 flex flex-col-reverse gap-1">
          {blackKeys.map((n) => (
            <React.Fragment key={n.note}>
              {/* Add spacing gap before F♯ to match piano layout */}
              {n.note === "F♯" && <div className="h-16" />}
              <RadioGroup.Item
                value={n.note}
                width="full"
                transform={n.note.includes("♯") ? "-translate-y-8" : ""}
                label={n.note}
                className="bg-black"
              />
            </React.Fragment>
          ))}
        </div>

        {/* Right column: White keys (naturals) */}
        <div className="w-1/2 flex flex-col-reverse gap-1">
          {whiteKeys.map((n) => (
            <RadioGroup.Item
              key={n.note}
              value={n.note}
              width="full"
              label={n.note}
              className="bg-gray-900"
            />
          ))}
        </div>
      </RadioGroup>

      {/* Horizontal layout (desktop) */}
      <RadioGroup
        value={selectedNote}
        onValueChange={handleNoteChange}
        className="hidden md:flex flex-col gap-2"
      >
        {/* Black keys row (sharps) */}
        <div className="flex gap-1">
          {blackKeys.map((n) => (
            <React.Fragment key={n.note}>
              {/* Add spacing gap before F♯ to match piano layout */}
              {n.note === "F♯" && <div className="w-16" />}
              <RadioGroup.Item
                value={n.note}
                width="sm"
                transform={n.note.includes("♯") ? "translate-x-8" : ""}
                label={n.note}
                className="bg-black text-gray-200"
              />
            </React.Fragment>
          ))}
        </div>

        {/* White keys row (naturals) */}
        <div className="flex gap-1">
          {whiteKeys.map((n) => (
            <RadioGroup.Item
              key={n.note}
              value={n.note}
              width="sm"
              label={n.note}
              className="bg-gray-900"
            />
          ))}
        </div>
      </RadioGroup>
    </>
  );
}
